/**
 * LayerManager - Base plane ("layer") creation, selection and visualization
 * Extracted from Application.js (pure code motion; delegation pattern).
 *
 * The state arrays (basePlanes, basePlaneSketchStates,
 * selectedBasePlaneIndices, numBasePlanes, basePlaneSpacing, mirrorMode,
 * planeVisualizations) stay on the Application - they are referenced from
 * many other places. This manager holds only the behavior.
 */
import * as THREE from 'three';
import { SlicePlane } from '../geometry/SlicePlane.js';

export class LayerManager {
    constructor(app) {
        this.app = app;
    }

    /**
     * Initialize base planes (the "layers" that contain sketches)
     * Creates parallel planes at different Z positions
     */
    initializeBasePlanes() {
        console.log(`Creating ${this.app.numBasePlanes} base planes...`);

        // Clear existing base planes
        this.app.basePlanes = [];
        this.app.basePlaneSketchStates = [];

        // Calculate spacing and positions
        const totalWidth = (this.app.numBasePlanes - 1) * this.app.basePlaneSpacing * 0.01; // Convert to world units
        const startZ = -totalWidth / 2;

        // Create base planes at different Z positions
        for (let i = 0; i < this.app.numBasePlanes; i++) {
            const zPos = startZ + (i * this.app.basePlaneSpacing * 0.01);

            // Create a plane perpendicular to Z axis (facing +Z, same as primary plane)
            const position = new THREE.Vector3(0, 0, zPos);
            const normal = new THREE.Vector3(0, 0, 1); // Facing +Z (same as primary plane)

            const basePlane = new SlicePlane({
                position: position,
                normal: normal,
                thickness: this.app.getEffectiveThickness?.() ?? this.app.materialThickness ?? 5.0
            });

            this.app.basePlanes.push(basePlane);
            // NOTE: Don't add base planes to SlicePlaneManager - they're a separate system
            // SlicePlaneManager only manages: 1 primary plane + N cross-slices

            console.log(`Created base plane ${i} at Z=${zPos.toFixed(2)}`);
        }

        // Wire up base planes to SlicePlaneManager for intersection checks
        this.app.slicePlaneManager.setBasePlanes(this.app.basePlanes);

        // Select ALL planes by default
        this.app.selectedBasePlaneIndices.clear();
        for (let i = 0; i < this.app.basePlanes.length; i++) {
            this.app.selectedBasePlaneIndices.add(i);
            this.app.basePlanes[i].select();
        }

        // LINKED MASTER STROKES: rebuild the stroke store for the new
        // layer set. Any content in the live sketch is adopted as masters
        // linked on ALL layers (per-layer divergence cannot survive a full
        // plane rebuild - same as the old stamping behavior).
        this.app.activeSketchLayerIndex = 0;
        if (this.app.strokeStore) {
            this.app.strokeStore.reset(this.app.basePlanes.length);
            // Force re-adoption: existing strokeIds are unknown to the
            // fresh store, so the sync registers them as new masters
            this.app.syncStrokeStore();
        }

        // Load the first plane's RESOLVED state into the SketchPlane
        if (this.app.basePlanes.length > 0) {
            this.app.loadLayerIntoSketch(0);

            // Generate 3D meshes for all planes if they have content
            this.app.updateMeshesFromAllSelectedPlanes();
        }

        console.log(`Base planes initialized: ${this.app.basePlanes.length} planes (all selected)`);

        // Visualize base planes
        this.visualizeBasePlanes();

        // Update status display
        this.app.updateStatusDisplay();
    }

    /**
     * Visualize base planes in the 3D scene
     */
    visualizeBasePlanes() {
        console.log('Visualizing base planes...');

        // Each base plane visualized - just render the sketches, no plane geometry
        this.app.basePlanes.forEach((basePlane, index) => {
            // Phase 3.5: Render sketch in 3D on this plane
            // COMMENTED OUT: 2D sketch rendering overlaps the 3D chair mesh
            // if (this.app.USE_UNIFIED_VIEW && this.app.sketchRenderer3D) {
            //     const sketchState = this.app.basePlaneSketchStates[index];
            //     if (sketchState && (sketchState.paths?.length > 0 || sketchState.splines?.length > 0 || sketchState.closedShapes?.length > 0)) {
            //         this.app.sketchRenderer3D.renderSketchToPlane(
            //             sketchState,
            //             basePlane,
            //             index,
            //             this.app.sceneManager.scene
            //         );
            //     }
            // }
        });
    }

    /**
     * Select a single base plane (deselects all others)
     * @param {number} index - Index of plane to select
     */
    selectBasePlane(index) {
        if (index < 0 || index >= this.app.basePlanes.length) return;

        // Commit any pending live-sketch changes into the stroke store
        // BEFORE the selection changes (diff-based - never clobbers
        // diverged/detached layers, unlike the old state stamping)
        this.app.syncStrokeStore();

        // Clear selection and select only the target plane
        this.app.selectedBasePlaneIndices.clear();
        this.app.selectedBasePlaneIndices.add(index);

        // Update plane selection state
        this.app.basePlanes.forEach((p, i) => {
            if (i === index) {
                p.select();
            } else {
                p.unselect();
            }
        });

        // Load the selected plane's RESOLVED sketch state
        if (index >= 0 && index < this.app.basePlaneSketchStates.length) {
            this.app.loadLayerIntoSketch(index);
            console.log(`Switched to plane ${index + 1}, loaded its sketch`);
        }

        // Re-visualize to update colors
        this.clearPlaneVisualizations();
        this.visualizeBasePlanes();

        // Canonical pipeline: selection changes re-gate leg overlays and
        // plane states, so everything downstream rebuilds consistently
        this.app.regenerateAll();

        // Update status display
        this.app.updateStatusDisplay();
    }

    /**
     * Select all base planes
     */
    selectAllPlanes() {
        // Commit any pending live-sketch changes into the stroke store
        // BEFORE the selection changes (diff-based, clobber-free)
        this.app.syncStrokeStore();

        // Select all planes
        this.app.selectedBasePlaneIndices.clear();
        for (let i = 0; i < this.app.basePlanes.length; i++) {
            this.app.selectedBasePlaneIndices.add(i);
            this.app.basePlanes[i].select();
        }

        // Load the first plane's RESOLVED sketch for display
        if (this.app.basePlanes.length > 0) {
            this.app.loadLayerIntoSketch(0);
            console.log('Selected all planes, showing first plane sketch');
        }

        // Re-visualize to update colors
        this.clearPlaneVisualizations();
        this.visualizeBasePlanes();

        // Canonical pipeline (see Application.regenerateAll)
        this.app.regenerateAll();

        // Update status display
        this.app.updateStatusDisplay();
    }

    /**
     * Update base plane positions without recreating them
     * Maintains sketch states and just repositions planes
     */
    updateBasePlanePositions() {
        const totalWidth = (this.app.numBasePlanes - 1) * this.app.basePlaneSpacing * 0.01; // Convert to world units
        const startZ = -totalWidth / 2;

        // Update each existing plane's position
        for (let i = 0; i < this.app.basePlanes.length; i++) {
            const zPos = startZ + (i * this.app.basePlaneSpacing * 0.01);
            const newPosition = new THREE.Vector3(0, 0, zPos);

            // Update the plane's position
            this.app.basePlanes[i].position.copy(newPosition);

            // Update the THREE.Plane object
            this.app.basePlanes[i].plane.setFromNormalAndCoplanarPoint(
                this.app.basePlanes[i].normal,
                newPosition
            );

            console.log(`Updated base plane ${i} to Z=${zPos.toFixed(2)}`);
        }

        // Update SlicePlaneManager with repositioned base planes
        this.app.slicePlaneManager.setBasePlanes(this.app.basePlanes);

        // Update visualizations
        this.clearPlaneVisualizations();
        this.visualizeBasePlanes();

        // POSITIONAL HELPER ONLY: no regeneration here. Callers (e.g.
        // Application.setChairWidth) run the canonical regenerateAll once
        // afterwards - regenerating from inside as well raced a second
        // pipeline pass against the caller's own.
    }

    /**
     * Add a new layer by copying an adjacent layer
     * Inserts the new layer next to the currently selected layer
     */
    addNewLayer() {
        // Get the currently selected layer (or use the first one if multiple selected)
        let sourceIndex = 0;
        if (this.app.selectedBasePlaneIndices.size > 0) {
            sourceIndex = Array.from(this.app.selectedBasePlaneIndices)[0];
        }

        // Insert new layer right after the source layer
        const insertIndex = sourceIndex + 1;

        // Commit any pending live-sketch changes into the stroke store,
        // then insert a store layer copying the source layer's instances
        // (linked instances SHARE the master; detached ones are cloned)
        this.app.syncStrokeStore();
        this.app.strokeStore?.insertLayer(insertIndex, sourceIndex);

        // Increment number of base planes
        this.app.numBasePlanes++;

        // Recalculate spacing to maintain chair width
        this.app.basePlaneSpacing = this.app.chairWidth / (this.app.numBasePlanes - 1);

        // Rebuild base planes with new count
        this.app.basePlanes = [];
        this.app.basePlaneSketchStates = [];

        const totalWidth = (this.app.numBasePlanes - 1) * this.app.basePlaneSpacing * 0.01;
        const startZ = -totalWidth / 2;

        // Create base planes at new positions
        for (let i = 0; i < this.app.numBasePlanes; i++) {
            const zPos = startZ + (i * this.app.basePlaneSpacing * 0.01);
            const position = new THREE.Vector3(0, 0, zPos);
            const normal = new THREE.Vector3(0, 0, 1);

            const basePlane = new SlicePlane({
                position: position,
                normal: normal,
                thickness: this.app.getEffectiveThickness?.() ?? this.app.materialThickness ?? 5.0
            });

            this.app.basePlanes.push(basePlane);
        }

        // LINKED MASTER STROKES: per-layer states are RESOLVED from the
        // stroke store (the insertLayer above already shifted the tables)
        this.app.basePlaneSketchStates = [];
        this.app.resolveLayerStates();

        // Wire up base planes
        this.app.slicePlaneManager.setBasePlanes(this.app.basePlanes);

        // Select the newly inserted layer
        this.app.selectedBasePlaneIndices.clear();
        this.app.selectedBasePlaneIndices.add(insertIndex);
        this.app.basePlanes[insertIndex].select();

        // Load the new layer's RESOLVED sketch state
        this.app.loadLayerIntoSketch(insertIndex);

        // Update visualizations
        this.clearPlaneVisualizations();
        this.visualizeBasePlanes();

        // ONE canonical regeneration: rebuilds slice groups AND leg braces
        // against the new layer set (leg braces previously went stale here
        // because only the regular pipeline ran)
        this.app.regenerateAll();

        console.log(`Added new layer at position ${insertIndex + 1}, copied from layer ${sourceIndex + 1}`);

        // Update status display
        this.app.updateStatusDisplay();
    }

    /**
     * Toggle mirror mode on/off
     */
    toggleMirrorMode() {
        this.app.mirrorMode = !this.app.mirrorMode;
        console.log(`Mirror mode: ${this.app.mirrorMode ? 'ON' : 'OFF'}`);
        this.app.updateStatusDisplay();
    }

    /**
     * Toggle guide layer status for currently selected layers
     */
    toggleGuideLayer() {
        if (this.app.selectedBasePlaneIndices.size === 0) {
            console.log('No layers selected');
            return;
        }

        // Toggle guide status for all selected layers
        for (const index of this.app.selectedBasePlaneIndices) {
            if (index >= 0 && index < this.app.basePlanes.length) {
                this.app.basePlanes[index].guide = !this.app.basePlanes[index].guide;
                console.log(`Layer ${index + 1} guide status: ${this.app.basePlanes[index].guide ? 'ON' : 'OFF'}`);
            }
        }

        // Guide status changes fabricated geometry (guide planes have no
        // part/slots) - canonical pipeline
        this.clearPlaneVisualizations();
        this.visualizeBasePlanes();
        this.app.regenerateAll();
        this.app.updateStatusDisplay();
    }

    /**
     * Get the mirrored index for a given layer index
     * @param {number} index - Layer index
     * @returns {number} Mirrored layer index
     */
    getMirroredIndex(index) {
        // Mirror around the center of the layer array
        // For 5 layers (0,1,2,3,4): 0↔4, 1↔3, 2↔2
        return this.app.numBasePlanes - 1 - index;
    }

    /**
     * Select a base plane with mirror mode support
     * If mirror mode is on, also selects/unselects the mirrored layer
     * @param {number} index - Layer index to select
     * @param {boolean} toggle - If true, toggle selection instead of replacing
     */
    selectBasePlaneWithMirror(index, toggle = false) {
        if (index < 0 || index >= this.app.basePlanes.length) return;

        // Commit any pending live-sketch changes into the stroke store
        // BEFORE the selection changes (diff-based, clobber-free)
        this.app.syncStrokeStore();

        // Get mirrored index
        const mirroredIndex = this.getMirroredIndex(index);

        // Determine if we're toggling or replacing selection
        const isCurrentlySelected = this.app.selectedBasePlaneIndices.has(index);

        if (this.app.mirrorMode) {
            // Mirror mode: select/unselect both the layer and its mirror
            if (toggle) {
                // Toggle mode: flip selection state for both layers
                if (isCurrentlySelected) {
                    this.app.selectedBasePlaneIndices.delete(index);
                    this.app.basePlanes[index].unselect();
                    if (mirroredIndex !== index) {
                        this.app.selectedBasePlaneIndices.delete(mirroredIndex);
                        this.app.basePlanes[mirroredIndex].unselect();
                    }
                } else {
                    this.app.selectedBasePlaneIndices.add(index);
                    this.app.basePlanes[index].select();
                    if (mirroredIndex !== index) {
                        this.app.selectedBasePlaneIndices.add(mirroredIndex);
                        this.app.basePlanes[mirroredIndex].select();
                    }
                }
            } else {
                // Replace mode: select only these layers
                this.app.selectedBasePlaneIndices.clear();
                this.app.basePlanes.forEach(p => p.unselect());

                this.app.selectedBasePlaneIndices.add(index);
                this.app.basePlanes[index].select();
                if (mirroredIndex !== index) {
                    this.app.selectedBasePlaneIndices.add(mirroredIndex);
                    this.app.basePlanes[mirroredIndex].select();
                }
            }

            // Load the primary selected plane's RESOLVED sketch
            if (this.app.selectedBasePlaneIndices.has(index)) {
                this.app.loadLayerIntoSketch(index);
            } else if (this.app.selectedBasePlaneIndices.size > 0) {
                // Mirror-toggle deselected the clicked pair: keep the live
                // sketch mirroring a layer that is still selected
                this.app.loadLayerIntoSketch(Math.min(...this.app.selectedBasePlaneIndices));
            }
            console.log(`Selected layer ${index + 1} and mirrored layer ${mirroredIndex + 1}`);
        } else if (toggle) {
            // Normal mode + shift: toggle this layer in the multi-selection
            // (keep at least one layer selected)
            if (isCurrentlySelected) {
                if (this.app.selectedBasePlaneIndices.size > 1) {
                    this.app.selectedBasePlaneIndices.delete(index);
                    this.app.basePlanes[index].unselect();
                    // If the live sketch was mirroring the deselected layer,
                    // reload it from a layer that is still selected
                    if (this.app.activeSketchLayerIndex === index) {
                        this.app.loadLayerIntoSketch(Math.min(...this.app.selectedBasePlaneIndices));
                    }
                }
            } else {
                this.app.selectedBasePlaneIndices.add(index);
                this.app.basePlanes[index].select();
                // Load the newly added plane's RESOLVED sketch as the active one
                this.app.loadLayerIntoSketch(index);
            }
            console.log(`Layer ${index + 1} ${this.app.selectedBasePlaneIndices.has(index) ? 'added to' : 'removed from'} selection (${this.app.selectedBasePlaneIndices.size} selected)`);
        } else {
            // Normal mode: use existing selectBasePlane method
            this.selectBasePlane(index);
            this.app.updateLayerMeshOpacity?.();
            return;
        }

        // Update visualizations + canonical pipeline
        this.clearPlaneVisualizations();
        this.visualizeBasePlanes();
        this.app.regenerateAll();
        this.app.updateStatusDisplay();
    }

    /**
     * Clear plane visualizations
     */
    clearPlaneVisualizations() {
        this.app.planeVisualizations.forEach(viz => {
            if (viz.mesh) {
                this.app.sceneManager.remove(viz.mesh);
                viz.mesh.geometry.dispose();
                viz.mesh.material.dispose();
            }
            if (viz.wireframe) {
                this.app.sceneManager.remove(viz.wireframe);
                viz.wireframe.geometry.dispose();
                viz.wireframe.material.dispose();
            }
        });
        this.app.planeVisualizations = [];

        // Phase 3.5: Clear 3D sketch renderings
        if (this.app.USE_UNIFIED_VIEW && this.app.sketchRenderer3D) {
            this.app.sketchRenderer3D.clearAll(this.app.sceneManager.scene);
        }
    }
}
